import { useState } from "react"
import {
  Plug,
  Phone,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
  Settings2,
  ArrowLeftRight,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import {
  useIntegrations,
  useCreateIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
  useTestIntegration,
  useSyncIntegration,
  useIntegrationLogs,
  type IntegrationConfig,
  type IntegrationSyncLog,
} from "@/hooks/use-integrations"

// ── Constants ────────────────────────────────────────────────────

const PROVIDER_INFO: Record<
  string,
  { label: string; description: string; color: string; bg: string }
> = {
  fakturownia: {
    label: "Fakturownia.pl",
    description: "Synchronizacja faktur z serwisem Fakturownia.pl",
    color: "#2563EB",
    bg: "#2563EB15",
  },
  sente: {
    label: "Sente ERP",
    description:
      "Dwukierunkowa synchronizacja z systemem Sente ERP (kontrahenci, produkty, faktury, stany magazynowe)",
    color: "#059669",
    bg: "#05966915",
  },
  telestrada: {
    label: "Telestrada VoIP",
    description:
      "Integracja telefoniczna — click-to-call, historia połączeń, nagrywanie rozmów",
    color: "#7C3AED",
    bg: "#7C3AED15",
  },
}

const SYNC_DIRECTION_LABELS: Record<
  string,
  { label: string; icon: typeof ArrowLeftRight }
> = {
  bidirectional: { label: "Dwukierunkowa", icon: ArrowLeftRight },
  push: { label: "Tylko eksport (CRM → ERP)", icon: ArrowRight },
  pull: { label: "Tylko import (ERP → CRM)", icon: ArrowLeft },
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  success: { label: "Sukces", color: "var(--color-success)" },
  error: { label: "Błąd", color: "var(--color-error)" },
  skipped: { label: "Pominięto", color: "var(--text-muted)" },
}

// ── Sente Config Form ────────────────────────────────────────────

interface SenteConfigFormData {
  base_url: string
  api_key: string
  api_secret: string
  company_id: string
  sync_products: boolean
  sync_customers: boolean
  sync_invoices: boolean
  sync_orders: boolean
}

const EMPTY_SENTE_CONFIG: SenteConfigFormData = {
  base_url: "",
  api_key: "",
  api_secret: "",
  company_id: "",
  sync_products: true,
  sync_customers: true,
  sync_invoices: true,
  sync_orders: true,
}

function SenteConfigDialog({
  open,
  integration,
  onClose,
}: {
  open: boolean
  integration: IntegrationConfig | null
  onClose: () => void
}) {
  const createMutation = useCreateIntegration()
  const updateMutation = useUpdateIntegration()
  const isEdit = !!integration

  const [form, setForm] = useState<SenteConfigFormData>(EMPTY_SENTE_CONFIG)
  const [syncDirection, setSyncDirection] = useState("bidirectional")

  const handleSave = () => {
    if (!form.base_url.trim()) {
      toast.error("Adres URL serwera jest wymagany")
      return
    }
    if (!form.api_key.trim()) {
      toast.error("Klucz API jest wymagany")
      return
    }

    const payload = {
      is_active: true,
      sync_direction: syncDirection,
      config: { ...form },
    }

    if (isEdit) {
      updateMutation.mutate(
        { id: integration.id, data: payload },
        {
          onSuccess: () => {
            toast.success("Konfiguracja Sente ERP zaktualizowana")
            onClose()
          },
          onError: () => toast.error("Nie udało się zaktualizować konfiguracji"),
        }
      )
    } else {
      createMutation.mutate(
        { provider: "sente", ...payload },
        {
          onSuccess: () => {
            toast.success("Integracja Sente ERP skonfigurowana")
            onClose()
          },
          onError: () => toast.error("Nie udało się utworzyć integracji"),
        }
      )
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edytuj konfigurację Sente ERP" : "Skonfiguruj Sente ERP"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Server URL */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Adres URL serwera Sente *
            </label>
            <input
              type="url"
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              placeholder="https://twoja-firma.sente.pl/api"
              className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
            />
          </div>

          {/* API Key + Secret */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Klucz API *
              </label>
              <input
                type="password"
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="Klucz API"
                className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Sekret API
              </label>
              <input
                type="password"
                value={form.api_secret}
                onChange={(e) =>
                  setForm({ ...form, api_secret: e.target.value })
                }
                placeholder="Sekret API"
                className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>
          </div>

          {/* Company ID */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              ID firmy w Sente
            </label>
            <input
              type="text"
              value={form.company_id}
              onChange={(e) =>
                setForm({ ...form, company_id: e.target.value })
              }
              placeholder="Identyfikator firmy"
              className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
            />
          </div>

          {/* Sync Direction */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Kierunek synchronizacji
            </label>
            <div className="space-y-2">
              {(["bidirectional", "push", "pull"] as const).map((dir) => {
                const info = SYNC_DIRECTION_LABELS[dir]
                const Icon = info.icon
                return (
                  <label
                    key={dir}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                      syncDirection === dir ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <input
                      type="radio"
                      name="sync_direction"
                      value={dir}
                      checked={syncDirection === dir}
                      onChange={() => setSyncDirection(dir)}
                      className="sr-only"
                    />
                    <div
                      className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                        syncDirection === dir ? "border-primary" : "border-border"
                      }`}
                    >
                      {syncDirection === dir && (
                        <div
                          className="h-2 w-2 rounded-full bg-primary"
                        />
                      )}
                    </div>
                    <Icon size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">
                      {info.label}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Sync Modules */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Moduły do synchronizacji
            </label>
            <div className="grid grid-cols-2 gap-2">
              {([
                { key: "sync_customers", label: "Kontrahenci" },
                { key: "sync_products", label: "Produkty" },
                { key: "sync_invoices", label: "Faktury" },
                { key: "sync_orders", label: "Zamówienia" },
              ] as const).map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors text-foreground ${
                    form[key] ? "border-primary bg-primary/5" : "border-border"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={(e) =>
                      setForm({ ...form, [key]: e.target.checked })
                    }
                    className="rounded accent-primary"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Telestrada Config Dialog ─────────────────────────────────────

interface TelestradaConfigFormData {
  api_key: string
  api_url: string
  caller_id: string
  webhook_secret: string
  record_calls: boolean
  auto_log_calls: boolean
}

const EMPTY_TELESTRADA_CONFIG: TelestradaConfigFormData = {
  api_key: "",
  api_url: "https://api.telestrada.pl",
  caller_id: "",
  webhook_secret: "",
  record_calls: true,
  auto_log_calls: true,
}

function TelestradaConfigDialog({
  open,
  integration,
  onClose,
}: {
  open: boolean
  integration: IntegrationConfig | null
  onClose: () => void
}) {
  const createMutation = useCreateIntegration()
  const updateMutation = useUpdateIntegration()
  const isEdit = !!integration

  const [form, setForm] = useState<TelestradaConfigFormData>(EMPTY_TELESTRADA_CONFIG)

  const handleSave = () => {
    if (!form.api_key.trim()) {
      toast.error("Klucz API jest wymagany")
      return
    }

    const payload = {
      is_active: true,
      sync_direction: "push" as const,
      config: { ...form },
    }

    if (isEdit) {
      updateMutation.mutate(
        { id: integration.id, data: payload },
        {
          onSuccess: () => {
            toast.success("Konfiguracja Telestrada zaktualizowana")
            onClose()
          },
          onError: () => toast.error("Nie udało się zaktualizować konfiguracji"),
        }
      )
    } else {
      createMutation.mutate(
        { provider: "telestrada", ...payload },
        {
          onSuccess: () => {
            toast.success("Integracja Telestrada skonfigurowana")
            onClose()
          },
          onError: () => toast.error("Nie udało się utworzyć integracji"),
        }
      )
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? "Edytuj konfigurację Telestrada VoIP"
              : "Skonfiguruj Telestrada VoIP"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* API URL */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Adres API
            </label>
            <input
              type="url"
              value={form.api_url}
              onChange={(e) => setForm({ ...form, api_url: e.target.value })}
              placeholder="https://api.telestrada.pl"
              className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
            />
          </div>

          {/* API Key */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Klucz API *
            </label>
            <input
              type="password"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder="Klucz API Telestrada"
              className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
            />
          </div>

          {/* Caller ID + Webhook Secret */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Numer wychodzący (Caller ID)
              </label>
              <input
                type="tel"
                value={form.caller_id}
                onChange={(e) =>
                  setForm({ ...form, caller_id: e.target.value })
                }
                placeholder="+48..."
                className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Sekret webhooka
              </label>
              <input
                type="password"
                value={form.webhook_secret}
                onChange={(e) =>
                  setForm({ ...form, webhook_secret: e.target.value })
                }
                placeholder="Opcjonalny"
                className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>
          </div>

          {/* Options */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Opcje
            </label>
            <div className="space-y-2">
              {([
                { key: "record_calls", label: "Nagrywanie rozmów" },
                {
                  key: "auto_log_calls",
                  label: "Automatyczne logowanie połączeń jako aktywności",
                },
              ] as const).map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors text-foreground ${
                    form[key] ? "border-primary bg-primary/5" : "border-border"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={(e) =>
                      setForm({ ...form, [key]: e.target.checked })
                    }
                    className="rounded accent-primary"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Sync Logs Panel ──────────────────────────────────────────────

function SyncLogsPanel({ integrationId }: { integrationId: string }) {
  const { data, isLoading } = useIntegrationLogs(integrationId, { limit: 20 })
  const logs = data?.data || []

  if (isLoading) {
    return (
      <div className="space-y-2 pt-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-full rounded-md" />
        ))}
      </div>
    )
  }

  if (logs.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">
        Brak logów synchronizacji
      </p>
    )
  }

  return (
    <div className="max-h-64 space-y-1.5 overflow-y-auto pt-3">
      {logs.map((log) => (
        <LogEntry key={log.id} log={log} />
      ))}
    </div>
  )
}

function LogEntry({ log }: { log: IntegrationSyncLog }) {
  const [expanded, setExpanded] = useState(false)
  const status = STATUS_LABELS[log.status] || {
    label: log.status,
    color: "var(--text-muted)",
  }

  return (
    <div className="rounded-md border border-border px-3 py-2 bg-background">
      <div
        className="flex cursor-pointer items-center justify-between gap-2"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: status.color }}
          />
          <span className="text-xs font-medium text-foreground">
            {log.action === "push" ? "Eksport" : log.action === "pull" ? "Import" : "Webhook"}
          </span>
          {log.entity_type && (
            <span className="text-xs text-muted-foreground">
              {log.entity_type}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">
            {new Date(log.created_at).toLocaleString("pl-PL")}
          </span>
          {expanded ? (
            <ChevronUp size={12} className="text-muted-foreground" />
          ) : (
            <ChevronDown size={12} className="text-muted-foreground" />
          )}
        </div>
      </div>
      {expanded && (
        <div className="mt-2 space-y-1 border-t border-border pt-2">
          {log.error_message && (
            <p className="text-xs text-destructive">
              {log.error_message}
            </p>
          )}
          {log.external_id && (
            <p className="text-xs text-muted-foreground">
              ID zewnętrzny: {log.external_id}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────

export function IntegrationsPage() {
  const { data, isLoading } = useIntegrations()
  const deleteMutation = useDeleteIntegration()
  const testMutation = useTestIntegration()
  const syncMutation = useSyncIntegration()
  const [configDialog, setConfigDialog] = useState<{
    open: boolean
    provider: string
    integration: IntegrationConfig | null
  }>({ open: false, provider: "", integration: null })
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [logsOpen, setLogsOpen] = useState<string | null>(null)

  const integrations = data?.data || []

  const handleTest = (integration: IntegrationConfig) => {
    testMutation.mutate(integration.id, {
      onSuccess: (res) => {
        if (res.data.success) {
          toast.success(res.data.message || "Połączenie działa poprawnie")
        } else {
          toast.error(res.data.error || "Test połączenia nie powiódł się")
        }
      },
      onError: () => toast.error("Nie udało się przetestować połączenia"),
    })
  }

  const handleSync = (integration: IntegrationConfig) => {
    syncMutation.mutate(integration.id, {
      onSuccess: (res) => {
        toast.success(res.data.message || "Synchronizacja uruchomiona")
      },
      onError: () => toast.error("Nie udało się uruchomić synchronizacji"),
    })
  }

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Integracja usunięta")
        setDeleteConfirm(null)
      },
      onError: () => toast.error("Nie udało się usunąć integracji"),
    })
  }

  // Find existing integrations by provider
  const senteIntegration = integrations.find((i) => i.provider === "sente")
  const fakturowniaIntegration = integrations.find(
    (i) => i.provider === "fakturownia"
  )
  const telestradaIntegration = integrations.find(
    (i) => i.provider === "telestrada"
  )

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="page-title">
          Integracje
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Połącz OkeyCRM z zewnętrznymi systemami ERP i fakturowania
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Sente ERP Card */}
          <IntegrationCard
            provider="sente"
            integration={senteIntegration || null}
            onConfigure={(integration) =>
              setConfigDialog({
                open: true,
                provider: "sente",
                integration,
              })
            }
            onTest={handleTest}
            onSync={handleSync}
            onDelete={(id) => setDeleteConfirm(id)}
            onToggleLogs={(id) =>
              setLogsOpen(logsOpen === id ? null : id)
            }
            logsOpen={logsOpen}
            testPending={testMutation.isPending}
            syncPending={syncMutation.isPending}
          />

          {/* Telestrada VoIP Card */}
          <IntegrationCard
            provider="telestrada"
            integration={telestradaIntegration || null}
            onConfigure={(integration) =>
              setConfigDialog({
                open: true,
                provider: "telestrada",
                integration,
              })
            }
            onTest={handleTest}
            onSync={handleSync}
            onDelete={(id) => setDeleteConfirm(id)}
            onToggleLogs={(id) =>
              setLogsOpen(logsOpen === id ? null : id)
            }
            logsOpen={logsOpen}
            testPending={testMutation.isPending}
            syncPending={syncMutation.isPending}
          />

          {/* Fakturownia Card */}
          <IntegrationCard
            provider="fakturownia"
            integration={fakturowniaIntegration || null}
            onConfigure={(integration) =>
              setConfigDialog({
                open: true,
                provider: "fakturownia",
                integration,
              })
            }
            onTest={handleTest}
            onSync={handleSync}
            onDelete={(id) => setDeleteConfirm(id)}
            onToggleLogs={(id) =>
              setLogsOpen(logsOpen === id ? null : id)
            }
            logsOpen={logsOpen}
            testPending={testMutation.isPending}
            syncPending={syncMutation.isPending}
          />
        </div>
      )}

      {/* Config Dialogs */}
      {configDialog.open && configDialog.provider === "sente" && (
        <SenteConfigDialog
          open
          integration={configDialog.integration}
          onClose={() =>
            setConfigDialog({ open: false, provider: "", integration: null })
          }
        />
      )}
      {configDialog.open && configDialog.provider === "telestrada" && (
        <TelestradaConfigDialog
          open
          integration={configDialog.integration}
          onClose={() =>
            setConfigDialog({ open: false, provider: "", integration: null })
          }
        />
      )}

      {/* Delete Confirmation */}
      <Dialog
        open={!!deleteConfirm}
        onOpenChange={(v) => !v && setDeleteConfirm(null)}
      >
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              Usuń integrację
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Czy na pewno chcesz usunąć tę integrację? Synchronizacja zostanie
            zatrzymana, ale istniejące dane nie zostaną usunięte.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Usuwanie..." : "Usuń"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Integration Card Component ───────────────────────────────────

function IntegrationCard({
  provider,
  integration,
  onConfigure,
  onTest,
  onSync,
  onDelete,
  onToggleLogs,
  logsOpen,
  testPending,
  syncPending,
}: {
  provider: string
  integration: IntegrationConfig | null
  onConfigure: (integration: IntegrationConfig | null) => void
  onTest: (integration: IntegrationConfig) => void
  onSync: (integration: IntegrationConfig) => void
  onDelete: (id: string) => void
  onToggleLogs: (id: string) => void
  logsOpen: string | null
  testPending: boolean
  syncPending: boolean
}) {
  const info = PROVIDER_INFO[provider]
  const isConfigured = !!integration
  const isActive = integration?.is_active ?? false

  return (
    <div className="rounded-lg border border-border surface-card">
      <div className="flex items-start gap-4 p-4">
        {/* Provider Icon */}
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: info.bg }}
        >
          {provider === "telestrada" ? (
            <Phone size={22} style={{ color: info.color }} />
          ) : (
            <Plug size={22} style={{ color: info.color }} />
          )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {info.label}
            </span>
            {isConfigured && (
              <Badge variant={isActive ? "default" : "secondary"} className="text-[10px]">
                {isActive ? "Aktywna" : "Nieaktywna"}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {info.description}
          </p>

          {isConfigured && integration.last_synced_at && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw size={10} />
              Ostatnia synchronizacja:{" "}
              {new Date(integration.last_synced_at).toLocaleString("pl-PL")}
            </p>
          )}

          {isConfigured && integration.sync_direction && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              {(() => {
                const dir = SYNC_DIRECTION_LABELS[integration.sync_direction]
                if (!dir) return null
                const Icon = dir.icon
                return (
                  <>
                    <Icon size={10} />
                    {dir.label}
                  </>
                )
              })()}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          {isConfigured ? (
            <>
              <button
                onClick={() => onTest(integration)}
                disabled={testPending}
                className="flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium transition-colors hover:opacity-80 disabled:opacity-50 text-muted-foreground"
                title="Test połączenia"
              >
                {testPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={12} />
                )}
                Test
              </button>
              <button
                onClick={() => onSync(integration)}
                disabled={syncPending}
                className="flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium transition-colors hover:opacity-80 disabled:opacity-50 text-muted-foreground"
                title="Synchronizuj teraz"
              >
                <RefreshCw
                  size={12}
                  className={syncPending ? "animate-spin" : ""}
                />
                Sync
              </button>
              <button
                onClick={() => onConfigure(integration)}
                className="flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:opacity-80 text-muted-foreground"
                title="Ustawienia"
              >
                <Settings2 size={14} />
              </button>
              <button
                onClick={() => onDelete(integration.id)}
                className="flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:opacity-80 text-destructive"
                title="Usuń"
              >
                <Trash2 size={14} />
              </button>
            </>
          ) : (
            <Button size="sm" onClick={() => onConfigure(null)}>
              Skonfiguruj
            </Button>
          )}
        </div>
      </div>

      {/* Logs Expandable */}
      {isConfigured && (
        <div className="border-t border-border px-4 py-2">
          <button
            onClick={() => onToggleLogs(integration.id)}
            className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground"
          >
            <span>Logi synchronizacji</span>
            {logsOpen === integration.id ? (
              <ChevronUp size={12} />
            ) : (
              <ChevronDown size={12} />
            )}
          </button>
          {logsOpen === integration.id && (
            <SyncLogsPanel integrationId={integration.id} />
          )}
        </div>
      )}
    </div>
  )
}
